// #32 — Recent plays of the signed-in user (lib/recentPlays.ts). Follows the auth store's userId: loaded at init and
// on every change; when the user goes away (logout) or is replaced, the previous user's list is cleared.
// Same signal as the react-query cache clear (lib/queryCacheOnLogout.ts).
import { create, type StoreApi, type UseBoundStore } from "zustand";
import { clearRecents, readRecents, withRecent, writeRecents, type RecentPlay } from "../lib/recentPlays";
import { useAuthStore } from "./authStore";

export interface RecentPlaysState {
  userId: string | null;
  items: RecentPlay[];
  /** No-op without a signed-in user. */
  record(entry: RecentPlay): void;
}

const initialUserId = useAuthStore.getState().userId;

export const useRecentPlaysStore: UseBoundStore<StoreApi<RecentPlaysState>> = create<RecentPlaysState>()((set, get) => ({
  userId: initialUserId,
  items: initialUserId ? readRecents(initialUserId) : [],
  record(entry) {
    const { userId, items } = get();
    if (!userId) return;
    const next = withRecent(items, entry);
    set({ items: next });
    writeRecents(userId, next);
  },
}));

useAuthStore.subscribe((s, prev) => {
  if (s.userId === prev.userId) return;
  if (prev.userId) clearRecents(prev.userId);
  useRecentPlaysStore.setState({ userId: s.userId, items: s.userId ? readRecents(s.userId) : [] });
});
